import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ArrowLeft, Rss, MapPin, Clock, Users, Video } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import VideoPlayer from '@/components/VideoPlayer';

const SocialFeed = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFeed();
  }, []);

  const fetchFeed = async () => {
    try {
      const response = await axios.get(`${API}/feed`);
      setFeed(response.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load feed');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white text-xl">Loading feed...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700">
        <div className="container mx-auto px-4 py-4 flex items-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-white hover:bg-white/10 mr-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <h1 className="text-xl font-bold text-white flex items-center gap-2" style={{ fontFamily: 'Space Grotesk' }}>
            <Rss className="w-6 h-6 text-purple-400" />
            Social Feed
          </h1>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-2xl">
        {feed.length === 0 ? (
          <Card className="glass p-8 border border-slate-700 text-center fade-in">
            <Users className="w-16 h-16 text-slate-500 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No Activity Yet</h3>
            <p className="text-slate-400 mb-4">
              Your friends&apos; check-ins will appear here. Add some friends to see their bar adventures!
            </p>
            <Button
              onClick={() => navigate('/friends')}
              className="bg-purple-600 hover:bg-purple-700"
            >
              Find Friends
            </Button>
          </Card>
        ) : (
          <div className="space-y-4">
            {feed.map((item, index) => (
              <Card
                key={`${item.checkin.id}-${index}`}
                className="glass p-4 border border-slate-700 fade-in hover:border-purple-500/50 transition-all cursor-pointer"
                onClick={() => navigate(`/bar/${item.bar?.id}`)}
              >
                {/* User Info */}
                <div className="flex items-center gap-3 mb-3">
                  <Avatar className="w-10 h-10 border-2 border-purple-500">
                    {item.user?.profile_picture_url ? (
                      <AvatarImage src={`${process.env.REACT_APP_BACKEND_URL}${item.user.profile_picture_url}`} />
                    ) : null}
                    <AvatarFallback className="bg-purple-600 text-white">
                      {item.user?.username?.[0]?.toUpperCase() || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <p className="font-semibold text-white">{item.user?.username}</p>
                    <p className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {formatDistanceToNow(new Date(item.checkin.timestamp), { addSuffix: true })}
                    </p>
                  </div>
                </div>

                {/* Check-in Content */}
                <div className="bg-slate-800/50 rounded-lg p-3 mb-3">
                  <div className="flex items-center gap-2 mb-2">
                    <MapPin className="w-4 h-4 text-purple-400" />
                    <span className="text-purple-400 font-semibold">Checked in at</span>
                  </div>
                  <h4 className="text-lg font-bold text-white mb-1">{item.bar?.name}</h4>
                  <p className="text-sm text-slate-400">{item.bar?.address}</p>
                </div>

                {/* Video if exists */}
                {item.checkin.video_url && (
                  <div className="mt-3">
                    <div className="flex items-center gap-2 mb-2 text-sm text-slate-400">
                      <Video className="w-4 h-4" />
                      Live from the bar
                    </div>
                    <VideoPlayer
                      videoUrl={`${process.env.REACT_APP_BACKEND_URL}${item.checkin.video_url}`}
                      videoTags={item.checkin.video_tags || []}
                      className="w-full rounded-lg"
                    />
                    {item.checkin.video_tags && item.checkin.video_tags.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {item.checkin.video_tags.map((tag, idx) => (
                          <span key={idx} className="text-xs bg-purple-600/20 text-purple-300 px-2 py-1 rounded-full">
                            @{tag.friend_username}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Tagged Friends */}
                {item.checkin.tagged_friends && item.checkin.tagged_friends.length > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-sm text-slate-400">
                    <Users className="w-4 h-4" />
                    <span>with {item.checkin.tagged_friends.length} friend(s)</span>
                  </div>
                )}
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default SocialFeed;
